import { combineReducers, Reducer } from "redux";
import { produce } from "immer";

import { IFormClosedAction, IFormCustomTitleChangedAction, IFormSwitchedAction, ISelectedProfileChangedAction } from "@multiweb/reactCore/bridge";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "@multiweb/reactUtils";
import { IOpenedFormInfo } from "@multiweb/types/multiweb";
import { TypedObject } from "@multiweb/types/react";
import { IMenuItemDefaultActionAllowedAction, MenuItemActivatedType } from "../types";
import { formOpenedAndShowInMenuActionPattern } from "../utils";
import {
    CustomTitles,
    FormInstanceGroupMap,
    IMenuOpenedItemsState,
    InstanceNumberMap,
    INumbering,
    IRemoveGroupMapItemAction,
    IRemoveLabelInfoAction,
    IRemoveNumberingGroupAction,
    IRemoveOpenedItemNumberAction,
    ISetGroupMapItemAction,
    ISetLabelInfoAction,
    ISetNumberingGroupAction,
    ISetOpenedItemNumberAction,
    LabelValues,
    NumberingGroups,
} from "./types";

export const openedFormInstanceId: Reducer<string | null> = (state = null, action) => {
    if (action instanceof IFormSwitchedAction) {
        return action.payload.switchedFormInfo.formInstanceId;
    } else if (formOpenedAndShowInMenuActionPattern(action)) {
        if (action.payload.openInBackground)
            return state;
        return action.payload.openedFormInfo.formInstanceId;
    } else if (action instanceof IMenuItemDefaultActionAllowedAction && action.payload.itemType === MenuItemActivatedType.logoButton) {
        return null;
    } else if (action instanceof IFormClosedAction) {
        if (state === action.payload.closedFormInfo.formInstanceId) {
            return null;
        } else {
            return state;
        }
    } else {
        return state;
    }
};

export const openedFormsIds: Reducer<readonly string[]> = (state = EMPTY_ARRAY, action) => {
    if (formOpenedAndShowInMenuActionPattern(action)) {
        const { formInstanceId } = action.payload.openedFormInfo;
        return [formInstanceId, ...state.filter((filteredFormInstanceId) => filteredFormInstanceId !== formInstanceId)];
    } else if (action instanceof IFormClosedAction) {
        const closedFormId = action.payload.closedFormInfo.formInstanceId;
        return state.filter((filteredFormInstanceId) => closedFormId !== filteredFormInstanceId);
    } else {
        return state;
    }
};

export const openedFormsInfo: Reducer<TypedObject<IOpenedFormInfo>> = (state = EMPTY_OBJECT, action) => {
    if (formOpenedAndShowInMenuActionPattern(action)) {
        return produce(state, (draft) => {
            const { openedFormInfo } = action.payload;
            draft[openedFormInfo.formInstanceId] = openedFormInfo;
        });
    } else if (action instanceof IFormClosedAction) {
        return produce(state, (draft) => {
            const { closedFormInfo } = action.payload;
            delete draft[closedFormInfo.formInstanceId];
        });
    } else if (action instanceof ISelectedProfileChangedAction) {
        return produce(state, (draft) => {
            const { profile, formInstanceId } = action.payload;
            const openedFormInfo = draft[formInstanceId];
            if (openedFormInfo) {
                openedFormInfo.profileId = profile?.id;
                draft[formInstanceId] = openedFormInfo;
            }
        });
    } else {
        return state;
    }
};

export const customTitles: Reducer<CustomTitles> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IFormCustomTitleChangedAction) {
        return produce(state, (draft) => {
            const { formInstanceId, customTitle } = action.payload;
            draft[formInstanceId] = customTitle;
        });
    } else {
        return state;
    }
};

export const labelValues: Reducer<LabelValues> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetLabelInfoAction) {
        return produce(state, (draft) => {
            const { formInstanceId, labelValue, isLocKey } = action.payload;
            draft[formInstanceId] = {
                labelValue,
                isLocKey,
            };
        });
    } else if (action instanceof IRemoveLabelInfoAction) {
        return produce(state, (draft) => {
            delete draft[action.payload.formInstanceId];
        });
    } else {
        return state;
    }
};

export const numbers: Reducer<InstanceNumberMap> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetOpenedItemNumberAction) {
        return produce(state, (draft) => {
            const { formInstanceId, number } = action.payload;
            draft[formInstanceId] = number;
        });
    } else if (action instanceof IRemoveOpenedItemNumberAction) {
        return produce(state, (draft) => {
            delete draft[action.payload.formInstanceId];
        });
    } else {
        return state;
    }
};

export const groups: Reducer<NumberingGroups> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetNumberingGroupAction) {
        return produce(state, (draft) => {
            const { groupKey, groupData } = action.payload;
            draft[groupKey] = groupData;
        });
    } else if (action instanceof IRemoveNumberingGroupAction) {
        return produce(state, (draft) => {
            delete draft[action.payload.groupKey];
        });
    } else {
        return state;
    }
};

export const groupMap: Reducer<FormInstanceGroupMap> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof ISetGroupMapItemAction) {
        return produce(state, (draft) => {
            const { formInstanceId, groupKey } = action.payload;
            draft[formInstanceId] = groupKey;
        });
    } else if (action instanceof IRemoveGroupMapItemAction) {
        return produce(state, (draft) => {
            delete draft[action.payload.formInstanceId];
        });
    } else {
        return state;
    }
};

export const numbering: Reducer<INumbering> = combineReducers({
    numbers,
    groups,
    groupMap,
});

export const reducer: Reducer<IMenuOpenedItemsState> = combineReducers({
    openedFormInstanceId,
    openedFormsIds,
    openedFormsInfo,
    customTitles,
    labelValues,
    numbering,
});
